import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Upload, Film, Loader2, X, Play, ChevronRight, AlertCircle,
  Camera, Sparkles, Clock, Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';
import { useVideoMovementAnalyzer } from '../hooks/useVideoMovementAnalyzer';
import { analyzeMovement } from '../lib/analyzers';
import { buildSessionsAnalysis } from '../lib/videoSessionsAnalyzer';
import AnalyzerReport from '../components/AnalyzerReport';

/**
 * /analyze — Movement Analyser hub.
 *
 * Three vertically-stacked states inside a single page (driven by `mode`):
 *   - "list"    → history of past analyses + "Start new analysis"
 *   - "pick"    → movement selector + video file picker + "Analyse"
 *   - "process" → live MediaPipe processing + skeleton overlay + progress
 *   - "report"  → inline report (also savable). Saved reports get an /analyze/:id route.
 *
 * Privacy rule: the uploaded video file NEVER leaves the browser. We only
 * send numeric metrics + sampled keyframe landmarks to the server.
 */

const Analyze = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('list');
  const [catalog, setCatalog] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [customMovementName, setCustomMovementName] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoURL, setVideoURL] = useState(null);
  const [report, setReport] = useState(null);
  const [reportSaved, setReportSaved] = useState(false);
  const [interpreting, setInterpreting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const {
    phase, progress, error,
    videoRef, canvasRef,
    analyze, cancel,
  } = useVideoMovementAnalyzer();

  // Load catalog + history once
  useEffect(() => {
    (async () => {
      try {
        const [catRes, histRes] = await Promise.all([
          api.getMovementCatalog(),
          api.getMovementAnalysisHistory(25),
        ]);
        setCatalog(catRes.data?.movements || []);
        setHistory(histRes.data?.analyses || []);
      } catch (e) {
        toast.error('Could not load Movement Analyser data.');
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, []);

  const resetPicker = useCallback(() => {
    setSelectedMovement(null);
    setCustomMovementName('');
    if (videoURL) URL.revokeObjectURL(videoURL);
    setVideoFile(null);
    setVideoURL(null);
    setReport(null);
    setReportSaved(false);
  }, [videoURL]);

  const onFilePicked = (file) => {
    if (!file) return;
    if (!/^video\//.test(file.type)) {
      toast.error('Please pick an mp4, mov or webm video.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Keep videos under 100 MB — shorter clips analyse better anyway.');
      return;
    }
    if (videoURL) URL.revokeObjectURL(videoURL);
    setVideoFile(file);
    setVideoURL(URL.createObjectURL(file));
  };

  const startAnalysis = async () => {
    if (!selectedMovement) { toast.error('Pick a movement first.'); return; }
    if (selectedMovement.id === 'other' && !customMovementName.trim()) {
      toast.error('Name the movement you uploaded.');
      return;
    }
    if (!videoRef.current) { toast.error('Video element not ready.'); return; }
    setMode('process');
    const frames = await analyze(videoRef.current, { fps: 10 });
    if (!frames || frames.length === 0) {
      toast.error('No frames could be processed. Try a clearer clip.');
      setMode('pick');
      return;
    }
    const metrics = analyzeMovement(selectedMovement.analyzer, frames);
    const exerciseName = selectedMovement.id === 'other'
      ? customMovementName.trim()
      : selectedMovement.name;
    // Fetch the SAME exercise definition live Sessions use so the report
    // shows identical joint-angle rows, ideal ranges, phases and view tags.
    let definition = null;
    try {
      const defRes = await api.getExerciseDefinition(exerciseName);
      definition = defRes.data || null;
    } catch (e) {
      // Non-fatal — the legacy report still renders. We just lose the
      // Sessions-style cards for this one analysis.
      definition = null;
    }
    const sessionsAnalysis = definition
      ? buildSessionsAnalysis({ frames, definition, metrics, exerciseName })
      : null;
    setInterpreting(true);
    try {
      const res = await api.interpretMovement({
        movement_id: selectedMovement.id,
        custom_movement_name: selectedMovement.id === 'other' ? customMovementName.trim() : null,
        metrics,
        view_detected: metrics.view_detected || selectedMovement.best_view || 'side',
        sessions_analysis: sessionsAnalysis,
      });
      // Sample keyframes for storage (cap to 300 frames for smooth replay; drop landmarks3D to keep size sane)
      const stride = Math.max(1, Math.floor(frames.length / 300));
      const sampled = frames
        .filter((_, i) => i % stride === 0)
        .map(f => ({
          t: f.t,
          landmarks2D: f.landmarks2D?.map(p => ({
            x: Math.round(p.x * 1000) / 1000,
            y: Math.round(p.y * 1000) / 1000,
            // Keep visibility as null when MediaPipe doesn't supply it,
            // instead of clamping to 0 which would later fail the playback
            // filter and silently hide the entire skeleton.
            visibility: typeof p.visibility === 'number'
              ? Math.round(p.visibility * 100) / 100
              : null,
          })) || null,
        }));
      setReport({
        movement_id: selectedMovement.id,
        movement_label: res.data?.movement_label,
        custom_movement_name: selectedMovement.id === 'other' ? customMovementName.trim() : null,
        metrics,
        sessions_analysis: sessionsAnalysis
          ? { ...sessionsAnalysis, narrative: res.data?.narrative || null }
          : null,
        duration_seconds: videoRef.current.duration || null,
        frames_processed: frames.length,
        view_detected: metrics.view_detected,
        rep_count: metrics.rep_count,
        video_width: videoRef.current.videoWidth || null,
        video_height: videoRef.current.videoHeight || null,
        _landmarks_keyframes: sampled,
        _full_frames: frames,    // kept in-memory only — used by inline player while videoURL is still live
      });
      setMode('report');
    } catch (e) {
      toast.error('Could not generate the report. Try again.');
      setMode('pick');
    } finally {
      setInterpreting(false);
    }
  };

  const saveReport = async () => {
    if (!report || reportSaved || saving) return;
    // Diagnostic: count how many sampled frames actually carry landmark data.
    // If MediaPipe failed on every video frame for this clip we'd save an
    // empty skeleton timeline and the user would see a video with no overlay.
    // Surface this loudly instead of silently saving useless data.
    const kf = Array.isArray(report._landmarks_keyframes) ? report._landmarks_keyframes : [];
    const kfWithLm = kf.filter(f => Array.isArray(f?.landmarks2D) && f.landmarks2D.length > 0).length;
    // eslint-disable-next-line no-console
    console.info('[Analyzer save] keyframes:', kf.length, 'with-landmarks:', kfWithLm, 'reps:', report?.rep_count);
    if (kf.length === 0 || kfWithLm === 0) {
      toast.error('No skeleton frames captured for this clip — try a clearer recording with the full body visible.');
      return;
    }
    setSaving(true);
    setUploadProgress(0);
    let storage = null;
    try {
      // 1. Upload the raw clip to object storage so the saved /analyze/:id
      //    page can replay it in all 3 modes (video+skeleton / video only /
      //    skeleton only). The video never leaves the user's browser until
      //    they explicitly click Save.
      if (videoFile) {
        const up = await api.uploadMovementVideo(videoFile, (e) => {
          if (e.total) setUploadProgress(e.loaded / e.total);
        });
        storage = up.data || null;
      }
      const res = await api.saveMovementAnalysis({
        movement_id: report.movement_id,
        custom_movement_name: report.custom_movement_name,
        metrics: report.metrics,
        sessions_analysis: report.sessions_analysis,
        landmarks_keyframes: report._landmarks_keyframes,
        duration_seconds: report.duration_seconds,
        frames_processed: report.frames_processed,
        view_detected: report.view_detected,
        rep_count: report.rep_count,
        video_width: report.video_width,
        video_height: report.video_height,
        video_storage_path: storage?.storage_path || null,
        video_content_type: storage?.content_type || null,
        video_filename: storage?.filename || null,
        video_size_bytes: storage?.size || null,
      });
      setReportSaved(true);
      toast.success('Analysis saved.');
      const histRes = await api.getMovementAnalysisHistory(25);
      setHistory(histRes.data?.analyses || []);
      if (res.data?.id) navigate(`/analyze/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the analysis.');
    } finally {
      setSaving(false);
    }
  };

  const cleanReset = () => {
    cancel();
    resetPicker();
    setMode('list');
  };

  const deleteAnalysis = async (id) => {
    if (!window.confirm('Delete this analysis? The clip will be removed too.')) return;
    try {
      await api.deleteMovementAnalysis(id);
      const histRes = await api.getMovementAnalysisHistory(25);
      setHistory(histRes.data?.analyses || []);
      toast.success('Analysis deleted.');
    } catch (e) {
      toast.error('Could not delete the analysis.');
    }
  };

  // ─────────── RENDER ───────────
  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Revivo IQ</div>
            <h1 className="font-secondary font-bold text-3xl md:text-4xl tracking-tight" data-testid="analyze-title">
              MOVEMENT <span className="text-primary">ANALYSER</span>
            </h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
              Upload a clip, pick the movement, and get an AI biomechanics report — score, key findings,
              risk flags, and a next-session suggestion. Saved analyses keep your video for replay; delete the analysis to remove it.
            </p>
          </div>
          {mode !== 'list' && (
            <Button variant="ghost" onClick={cleanReset} data-testid="analyze-back-to-list">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>

        {mode === 'list' && (
          <div className="space-y-6">
            <Button
              size="lg"
              onClick={() => setMode('pick')}
              className="w-full md:w-auto bg-primary text-black hover:bg-primary/90"
              data-testid="analyze-start-new"
            >
              <Upload className="w-4 h-4 mr-2" /> Start a new analysis
            </Button>

            <div>
              <h2 className="font-secondary font-bold text-lg tracking-tight mb-3">RECENT ANALYSES</h2>
              {loadingHistory ? (
                <div className="hpme-card p-6 flex items-center gap-3 text-sm text-zinc-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
                </div>
              ) : history.length === 0 ? (
                <div className="hpme-card p-8 text-center" data-testid="analyze-empty-state">
                  <Film className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-400 text-sm">No analyses yet. Upload a clip to get your first report.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {history.map(h => (
                    <div
                      key={h.id}
                      className="hpme-card p-4 hover:border-primary/40 transition-colors flex items-center gap-3 group"
                      data-testid={`analyze-history-${h.id}`}
                    >
                      <Link to={`/analyze/${h.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">
                            {h.custom_movement_name || h.movement_id?.replace('_', ' ')}
                          </div>
                          <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                            <Clock className="w-3 h-3" />
                            {new Date(h.created_at).toLocaleString()}
                            {h.rep_count != null && <span>· {h.rep_count} reps</span>}
                            {h.video_storage_path && <span className="text-primary/70">· video</span>}
                          </div>
                        </div>
                        <div className="text-right pr-2">
                          <div className="font-secondary font-bold text-xl text-primary" data-testid={`analyze-history-score-${h.id}`}>
                            {h.sessions_analysis?.score != null ? Math.round(h.sessions_analysis.score) : '—'}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                            {h.sessions_analysis?.score != null
                              ? (h.sessions_analysis.score >= 90 ? 'Excellent'
                                : h.sessions_analysis.score >= 80 ? 'Good'
                                : h.sessions_analysis.score >= 60 ? 'Average'
                                : h.sessions_analysis.score >= 50 ? 'Below Avg'
                                : 'Poor')
                              : ''}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-zinc-600" />
                      </Link>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteAnalysis(h.id); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`analyze-history-delete-${h.id}`}
                        aria-label="Delete analysis"
                        title="Delete analysis"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {mode === 'pick' && (
          <div className="space-y-6">
            {/* Movement selector */}
            <div className="hpme-card p-6">
              <h2 className="font-secondary font-bold text-lg tracking-tight mb-4">1. CHOOSE MOVEMENT</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {catalog.map(m => (
                  <button
                    key={m.id}
                    onClick={() => setSelectedMovement(m)}
                    className={`p-3 rounded-sm border text-left transition-colors ${
                      selectedMovement?.id === m.id
                        ? 'border-primary bg-primary/10'
                        : 'border-zinc-800 hover:border-zinc-700'
                    }`}
                    data-testid={`analyze-movement-${m.id}`}
                  >
                    <div className="font-semibold text-sm">{m.name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1 flex items-center gap-1">
                      <Camera className="w-3 h-3" /> {m.best_view}
                    </div>
                  </button>
                ))}
              </div>
              {selectedMovement?.instructions && (
                <p className="text-xs text-zinc-400 mt-4">▸ {selectedMovement.instructions}</p>
              )}
              {selectedMovement?.id === 'other' && (
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Movement name</label>
                  <input
                    type="text"
                    value={customMovementName}
                    onChange={e => setCustomMovementName(e.target.value)}
                    placeholder="e.g. Turkish Get-Up"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm text-sm focus:border-primary focus:outline-none"
                    data-testid="analyze-custom-name-input"
                    maxLength={80}
                  />
                </div>
              )}
            </div>

            {/* Video picker */}
            <div className="hpme-card p-6">
              <h2 className="font-secondary font-bold text-lg tracking-tight mb-4">2. UPLOAD CLIP</h2>
              {!videoFile ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-zinc-700 hover:border-primary rounded-sm p-8 text-center transition-colors"
                  data-testid="analyze-file-picker-btn"
                >
                  <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                  <div className="text-sm font-semibold">Choose video</div>
                  <div className="text-xs text-zinc-500 mt-1">mp4, mov, webm · stored privately when you save · max 100 MB</div>
                </button>
              ) : (
                <div>
                  <video
                    ref={videoRef}
                    src={videoURL}
                    controls
                    playsInline
                    muted
                    className="w-full max-h-72 rounded-sm bg-black"
                    data-testid="analyze-video-preview"
                  />
                  <div className="flex items-center justify-between mt-2 text-xs text-zinc-500">
                    <span className="truncate">{videoFile.name}</span>
                    <button
                      onClick={() => { URL.revokeObjectURL(videoURL); setVideoFile(null); setVideoURL(null); }}
                      className="text-zinc-400 hover:text-white"
                      data-testid="analyze-clear-video"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/*"
                onChange={e => onFilePicked(e.target.files?.[0])}
                className="hidden"
                data-testid="analyze-file-input"
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={startAnalysis}
                disabled={!selectedMovement || !videoFile || (selectedMovement?.id === 'other' && !customMovementName.trim())}
                className="bg-primary text-black hover:bg-primary/90"
                data-testid="analyze-start-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Analyse
              </Button>
            </div>
          </div>
        )}

        {mode === 'process' && (
          <div className="hpme-card p-6" data-testid="analyze-processing">
            <h2 className="font-secondary font-bold text-lg tracking-tight mb-4">PROCESSING…</h2>
            <div className="relative bg-black rounded-sm overflow-hidden">
              <video
                ref={videoRef}
                src={videoURL}
                playsInline muted
                className="w-full max-h-96 object-contain relative"
                style={{ zIndex: 1 }}
              />
              <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
                // Force canvas above hardware-composited <video> (Safari/Chrome GPU layer bug).
                // mix-blend-mode: lighten ensures lime skeleton lights through
                // even if a stubborn browser still puts the video on top.
                style={{
                  zIndex: 2,
                  transform: 'translateZ(0)',
                  willChange: 'transform',
                  mixBlendMode: 'lighten',
                }}
              />
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-zinc-400">
                  {phase === 'loading' && 'Loading pose engine…'}
                  {phase === 'processing' && `Extracting movement data…`}
                  {interpreting && 'Generating coaching report…'}
                  {phase === 'error' && (error || 'Something went wrong.')}
                </span>
                <span className="text-primary font-secondary font-bold">{Math.round(progress * 100)}%</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
            </div>
            {phase === 'error' && (
              <div className="mt-4 flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button variant="ghost" onClick={cleanReset} data-testid="analyze-cancel-process">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {mode === 'report' && report && (
          <div className="space-y-4">
            <AnalyzerReport
              analysis={report}
              videoURL={videoURL}
              playerFrames={report._full_frames}
            />
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="ghost" onClick={() => { resetPicker(); setMode('pick'); }} data-testid="analyze-new-after-report">
                <Upload className="w-4 h-4 mr-2" /> Analyse another
              </Button>
              {!reportSaved && (
                <Button onClick={saveReport} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="analyze-save-report">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {uploadProgress > 0 && uploadProgress < 1
                        ? `Uploading ${Math.round(uploadProgress * 100)}%`
                        : 'Saving…'}
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" /> Save analysis
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Analyze;
